import React, { useState } from 'react';

import Button from 'CommonComponents/Button';
import FormInput from 'CommonComponents/FormInput';

/**
 * A single name field with Save and Cancel. Enter saves, Escape cancels.
 *
 * onSubmit returns a promise; a rejection carrying a validation response is
 * shown under the field, anything else is left to the caller to report.
 */
export default function InlineNameForm({ id, label, initialValue = '', placeholder, onSubmit, onCancel }) {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = (e) => {
        e?.preventDefault();

        const name = value.trim();
        if (!name) {
            setError($t('Name is required'));

            return;
        }

        setSubmitting(true);
        setError('');

        onSubmit(name)
            .catch((err) => {
                const errors = err?.response?.data?.errors;
                if (errors) {
                    setError(Object.values(errors).flat().join(' '));
                }
            })
            .finally(() => setSubmitting(false));
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
        }
    };

    return (
        <form className="tw-flex tw-items-start tw-gap-2 tw-w-full" onSubmit={submit}>
            <div className="tw-flex-1">
                <FormInput
                    id={id}
                    name={id}
                    type="text"
                    label={label}
                    hideLabel={true}
                    aria-label={label}
                    placeholder={placeholder}
                    maxLength={255}
                    autoFocus
                    value={value}
                    // readOnly, not disabled: a disabled field drops focus,
                    // and Escape would no longer reach it after a refusal.
                    readOnly={submitting}
                    showError={!!error}
                    errorText={error}
                    onKeyDown={onKeyDown}
                    onChange={(e) => {
                        setError('');
                        setValue(e.target.value);
                    }}
                />
            </div>
            <Button type="submit" variant="primary" small loading={submitting} disabled={submitting}>
                {$t('Save')}
            </Button>
            <Button variant="outline" small disabled={submitting} onClick={onCancel}>
                {$t('Cancel')}
            </Button>
        </form>
    );
}
